/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Select, { components } from 'react-select';
import { withStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import { Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import IntlMessages from 'util/IntlMessages';
import AsyncSelect from 'react-select/async';
import InputAdornment from '@mui/material/InputAdornment';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import './AsyncAutocompleteBoxComponent.scss';
import variables from 'styles/base/_variables.scss';
import { green } from '@mui/material/colors';
import AddNewOptions from 'modules/cbre-flow-add-new-options';
import { openAddNewOptionsDialog } from 'modules/cbre-flow-add-new-options/ducks/actions';
import { Button, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { isIncludeOption } from 'util/includeOptionHelpers';

const styles = (theme) => ({
  root: {
    flexGrow: 1,
  },
  input: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'clip',
    color: variables.colorTextDark,
    fontSize: variables.fontSizeBase,
    fontFamily: 'inherit',
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    overflow: 'hidden',
  },
  indicatorsContainer: {
    display: 'none !important',
  },
  noOptionsMessage: {},
  singleValue: {
    fontSize: `${variables.fontSizeBase} !important`,
    fontFamily: 'inherit',
  },
  placeholder: {},
  paper: {
    position: 'absolute',
    zIndex: "2 !important",
    marginTop: 1,
    left: 10,
    right: 0,
    width: 'calc(100% - 20px) !important',
  },
});

const DropdownIndicator = (props) =>
  props.isDisabled || props.selectProps.showDropDown === false ? null : (
    <components.DropdownIndicator {...props}>
      <i className="zmdi zmdi-caret-down zmdi-hc-fw zmdi-hc-lg" />
    </components.DropdownIndicator>
  );

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

const Input = (props) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <components.Input maxLength="100" {...props} style={{ margin: 0, marginTop: 5, display: 'block' }} />
  </div>
);

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}
const searchComponent = (props) => {
  if (props.selectProps.showSearchIcon) {
    return (
      <InputAdornment
        position="start"
        className={`${props.selectProps.value ? 'hidden' : 'async-search-icon'}`}
      >
        <ManageSearchIcon fontSize="small" />
      </InputAdornment>
    );
  }
};

const hasSelectedValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return !!value;
};

function Control(props) {
  const handleTextChange = () => {
    // Only single-select fields should clear their value when the user starts
    // typing a new search - for multi-select, typing must not wipe existing chips.
    if (props.selectProps.isMulti) return;
    if (props.getValue().length > 0) {
      props.clearValue();
    }
  };
  return (
    <TextField
      onChange={handleTextChange}
      error={props.selectProps.error}
      margin="dense"
      // label="Client*"
      label={props.selectProps.label}
      variant={props.variant || 'outlined'}
      fullWidth={true}
      // Inline (theme-independent) fix: multi-select boxes were rendering
      // shorter than single-select fields like site/building - the .scss
      // min-height depends on an ancestor ".green" theme class that isn't
      // reliably present.
      sx={
        props.selectProps.isMulti
          ? { '& .MuiInputBase-root': { minHeight: '44px' } }
          : undefined
      }
      InputProps={{
        inputComponent,
        inputProps: {
          root: props.selectProps.classes.root,
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
        className: props.selectProps.classes.inputBackground,
        endAdornment: (
          <>
            {props.selectProps.isLoading ? (
              <CircularProgress size={18} sx={{ mr: 1 }} />
            ) : (
              searchComponent(props)
            )}
            {props.selectProps.InputProps?.endAdornment || null}
          </>
        ),
        ...(props.selectProps.InputProps || {}),
      }}
      InputLabelProps={{
        shrink: !!(
          props.isFocused ||
          hasSelectedValue(props.selectProps.value) ||
          props.selectProps.isShrink
        ),
        // Inline (theme-independent) fix: mask the selected chip peeking through
        // behind the shrunk label and pin its position - the .scss rule depends
        // on an ancestor ".green" theme class that isn't reliably present.
        sx: {
          '&.MuiInputLabel-shrink': {
            backgroundColor: '#fff',
            paddingLeft: '4px',
            paddingRight: '4px',
            marginLeft: '-4px',
            zIndex: 1,
            transform: 'translate(14px, -9px) scale(0.75) !important',
            transformOrigin: 'top left',
          },
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  // console.log(props)
  return (
    <MenuItem
      title={props?.data?.email || props?.data?.contact?.email}
      buttonRef={props.innerRef}
      selected={props.isSelected}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
        fontSize: variables.fontSizeBase,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function MultiValue(props) {
  if (props.selectProps.roundedMultiValue) {
    return (
      <Chip
        label={props.children}
        variant="filled"
        deleteIcon={<CloseIcon />}
        onDelete={props.removeProps.onClick}
        onMouseDown={props.removeProps.onMouseDown}
        onTouchEnd={props.removeProps.onTouchEnd}
        tabIndex={-1}
        disabled={props.isDisabled || props.selectProps.isDisabled}
        sx={{
          height: 24,
          minHeight: 24,
          maxHeight: 24,
          border: 0,
          borderRadius: '999px',
          bgcolor: '#e3ebe8',
          color: '#1a1a1a',
          fontSize: variables.fontSizeBase,
          alignItems: 'center',
          '& .MuiChip-label': {
            px: 1.25,
            py: 0,
            paddingTop: '4px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1,
          },
          '& .MuiChip-deleteIcon': {
            color: '#1a1a1a',
            background: 'transparent !important',
            backgroundColor: 'transparent !important',
            boxShadow: 'none',
            borderRadius: '50%',
            width: 18,
            height: 18,
            fontSize: 18,
            m: '0 5px 0 0',
            alignSelf: 'center',
            '&:hover': {
              color: '#1a1a1a',
              background: 'transparent !important',
              backgroundColor: 'transparent !important',
            },
          },
          '&.Mui-disabled': {
            bgcolor: 'rgba(0, 0, 0, 0.08)',
            color: 'rgba(0, 0, 0, 0.38)',
            opacity: 1,
            '& .MuiChip-deleteIcon': {
              color: 'rgba(0, 0, 0, 0.38)',
            },
          },
        }}
      />
    );
  }
  return <components.MultiValue {...props} />;
}

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  const { selectProps, hasValue, getValue, children } = props;
  const shouldLimitChipsByWidth = selectProps.isMulti && selectProps.chipsVisibleLimit;
  const totalSelected = hasValue ? getValue().length : 0;
  const [visibleChipCount, setVisibleChipCount] = React.useState(totalSelected);
  const [containerWidth, setContainerWidth] = React.useState(0);
  const containerRef = React.useRef(null);
  // The Input (search caret) is always the last child - pull it out so the
  // "+N" badge can be placed before it instead of after.
  const childrenArray = React.Children.toArray(children);
  const inputElement = childrenArray.pop();
  const extraCount = Math.max(totalSelected - visibleChipCount, 0);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !shouldLimitChipsByWidth) return undefined;

    const updateContainerWidth = () => setContainerWidth(container.clientWidth);
    updateContainerWidth();
    const resizeObserver = new ResizeObserver(updateContainerWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [shouldLimitChipsByWidth]);

  React.useLayoutEffect(() => {
    setVisibleChipCount(totalSelected);
  }, [containerWidth, totalSelected]);

  React.useLayoutEffect(() => {
    if (!shouldLimitChipsByWidth || visibleChipCount !== totalSelected || !containerRef.current) return;

    const availableWidth = containerRef.current.clientWidth - 44;
    const chipElements = Array.from(containerRef.current.querySelectorAll('.async-autocomplete-chip'));
    const visibleCount = chipElements.reduce(
      (count, chipElement) => {
        const chipsWidth = chipElements
          .slice(0, count + 1)
          .reduce((width, chip) => width + chip.offsetWidth, 0);
        const hiddenChipCount = totalSelected - (count + 1);
        const overflowBadgeWidth = hiddenChipCount > 0 ? 40 : 0;
        return chipsWidth + overflowBadgeWidth <= availableWidth ? count + 1 : count;
      },
      0,
    );

    if (visibleCount !== visibleChipCount) setVisibleChipCount(visibleCount);
  }, [shouldLimitChipsByWidth, totalSelected, visibleChipCount]);

  return (
    <div ref={containerRef} className={`valueContainer ${selectProps.classes.valueContainer}`}>
      {!hasValue && selectProps.placeholder ? (
        <Typography
          color="textSecondary"
          className={selectProps.classes.placeholder}
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            fontSize: variables.fontSizeBase,
            fontFamily: 'inherit',
            fontWeight: 400,
          }}
        >
          {selectProps.placeholder}
        </Typography>
      ) : null}
      {childrenArray.slice(0, shouldLimitChipsByWidth ? visibleChipCount : childrenArray.length).map((child, index) => (
        <span className="async-autocomplete-chip" key={index}>{child}</span>
      ))}
      {shouldLimitChipsByWidth && extraCount > 0 ? (
        <span className="multi-value-more-badge">{`+${extraCount}`}</span>
      ) : null}
      {inputElement}
    </div>
  );
}

function Menu(props) {
  const dispatch = useDispatch();
  const { addNewOptions = {} } = props.selectProps;
  return (
    <components.Menu  {...props}  className={`custom-style ${props.selectProps.classes.paper} ${addNewOptions.enabled && props.placement === "bottom" ? 'mb-5': ''}`} >
      {/* <Paper
        square={true}
       
        {...props.innerProps}
      > */}
        {props.children}
        {addNewOptions?.enabled === true ? (
          <Button
            variant="outlined"
            color="primary"
            onClick={() =>
              dispatch(
                openAddNewOptionsDialog({
                  mode: addNewOptions.mode,
                  body: addNewOptions.body,
                  cb: addNewOptions.cb,
                }),
              )
            }
            className="pjm-btn pjm-btn-sm personnel-btn w-100"
            role="button"
          >
            <IntlMessages id="cbre-flow-add-new-options.title.addNew" />
          </Button>
        ) : (
          ''
        )}
        {props.selectProps.dropDownbutton}
      {/* </Paper> */}
    </components.Menu>
  );
}

function AsyncAutocompleteBoxComponent(props) {
  const [single, setSingle] = useState(null);
  const [multi, setMulti] = useState(null);
  const [refData, setRefData] = useState(null);
  const {
    classes,
    customStyles,
    showSearchIcon,
    theme,
    control,
    errors,
    errorMessages,
    isMultiError,
    name,
    id,
    rules,
    setValue,
    module,
    suggestions,
    defaultVal,
    updateDisplay,
    handleUpdateDisplay,
    updateDisplayValue,
    getOptionValue,
    getOptionLabel,
    mode,
    label,
    required,
    loadOptions,
    defaultOptions,
    disabled,
    menuIsOpen,
    onMenuClose,
    onMenuOpen,
    // autoFocus,
    focusField,
    FormHText,
    onHandleChange,
    dropDownbutton,
    isShrink,
    style = {},
    isClearable = true,
    intlValues,
    addNewOptionsParams,
    addNewOptions,
    isSearchable = true,
    closeMenuOnSelect = true,
    isLoading = false,
    validateChange,
    debounceMs = 0
  } = props;
  const [validationMessage, setValidationMessage] = useState('');
  const containerRef = useRef()
  const loadOptionsRef = useRef(loadOptions);
  loadOptionsRef.current = loadOptions;
  const debouncedLoadOptions = useMemo(
    () => (debounceMs > 0
      ? debounce((inputValue, callback) => loadOptionsRef.current?.(inputValue, callback), debounceMs)
      : null),
    [debounceMs],
  );
  useEffect(() => () => debouncedLoadOptions?.cancel(), [debouncedLoadOptions]);
  const effectiveLoadOptions = debouncedLoadOptions && loadOptions ? debouncedLoadOptions : loadOptions;
  const handleChange =
    (field) =>
    async (value, ...rest) => {
      if (validateChange) {
        const isValid = await validateChange(value);
        if (!isValid) {
          // If validation fails, reject the change
          return;
        }
      }
      let nextValue = value;
      if (props.isMulti && Array.isArray(value)) {
        const previousValue = Array.isArray(field.value) ? field.value : [];
        const isSameOption = (a, b) => {
          if (a === b) return true;
          if (getOptionLabel) {
            try {
              return getOptionLabel(a) === getOptionLabel(b);
            } catch (e) {
              return false;
            }
          }
          return false;
        };
        const addedItems = value.filter(
          (item) => !previousValue.some((prev) => isSameOption(prev, item)),
        );

        // "Include" picks are exclusive with every other option (in both
        // directions): selecting an include drops any other selections, and
        // selecting a regular option drops any include already selected.
        if (value.length > previousValue.length && addedItems.length > 0) {
          const addedIncludeItem = addedItems.find((item) => isIncludeOption(item));
          nextValue = addedIncludeItem
            ? [addedIncludeItem]
            : value.filter((item) => !isIncludeOption(item));
        }
      }
      setValue(name, nextValue, { shouldValidate: true });
      field.onChange && field.onChange(nextValue, ...rest);
      props.onChange && props.onChange(nextValue, ...rest);
    };

  useEffect(() => {
    if (errors && !isMultiError) {
      const val = module + errorMessages[errors];
      setValidationMessage(<IntlMessages id={val} values={intlValues} />);
    } else if (errors && isMultiError) {
      setValidationMessage(
        <IntlMessages id={errorMessages} values={intlValues} />,
      );
    } else {
      setValidationMessage('');
    }
  }, [errors]);

  useEffect(() => {
    setSingle(defaultVal);
    if (mode === 'edit') {
      console.log("setting defaultvalue --- ",defaultVal)
      setValue(name, defaultVal, { shouldValidate: true });
    }
  }, [mode]);

  useEffect(() => {
    setSingle(defaultVal);
    if (mode === 'edit') {
      setValue(name, defaultVal, { shouldValidate: true });
    }
  }, [JSON.stringify(defaultVal)]);

  useEffect(() => {
    if (focusField === name) {
      refData.focus();
    }
  }, [focusField]);

  useEffect(() => {
    if (updateDisplay) {
      setSingle(updateDisplayValue);
      handleUpdateDisplay();
    }
  }, [updateDisplay]);

  const selectStyles = {
    input: (base) => ({
      ...base,
      color: theme.palette.text.primary,
      margin: 0,
      paddingTop: 0,
      paddingBottom: 0,
      '& input': {
        fontSize: variables.fontSizeBase,
        fontFamily: 'inherit',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      width: '100%',
      ...(props.roundedMultiValue ? { paddingTop: 6 } : {}),
    }),

    indicatorsContainer: (base) => ({
      ...base,
      '& > div:first-child': {
        // paddingRight:0
      },
    }),

    dropdownIndicator: (base) => ({
      ...base,
      paddingRight: '0 !important',
      '&:hover': {
        color: 'rgba(0, 0, 0, 0.54)',
        cursor: 'pointer',
      },
      color: 'rgba(0, 0, 0, 0.54)',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: props.menuPortalTarget ? 1300 : 1,
    }),

    multiValueRemove: (styles, { data }) => ({
      ...styles,
      ':hover': {
        backgroundColor: green[800],
        color: 'white',
        cursor: 'pointer',
      },
    }),
  };

  const additionalProps = {};
  if (props.menuPortalTarget) {
    additionalProps.menuPortalTarget = props.menuPortalTarget;
    additionalProps.menuPosition = 'fixed';
  }

  if (props.InputProps) additionalProps.InputProps = props.InputProps;
  if (props.showDropDown !== undefined)
    additionalProps.showDropDown = props.showDropDown;
  if (props.value) additionalProps.value = props.value;
  if (props.defaultVal) additionalProps.defaultValue = props.defaultVal;
  if (props.isMulti) additionalProps.isMulti = props.isMulti;
  if (props.formatOptionLabel)
    additionalProps.formatOptionLabel = props.formatOptionLabel;
  if (props.chipsVisibleLimit) additionalProps.chipsVisibleLimit = props.chipsVisibleLimit;
  if (props.roundedMultiValue) additionalProps.roundedMultiValue = true;
  // The visible "input" is a div wrapper around react-select's own input, so
  // TextField's placeholder attribute never renders - forwarded as selectProps.placeholder
  // and rendered directly inside ValueContainer instead.
  if (props.placeholder) additionalProps.placeholder = props.placeholder;

  // Opt-in only (props.dedupeMultiSelect): some multi-select fields pass
  // getOptionValue={(data) => data}, so react-select's default equality (which
  // compares via getOptionValue) never matches identical options - allowing
  // duplicate chips and no "selected" highlight in the menu. Fields that already
  // supply a real stable getOptionValue don't need this and are left untouched.
  if (props.dedupeMultiSelect) {
    // react-select's own deselect-on-reclick logic (selectOption) filters the
    // selected values using getOptionValue equality - so it must resolve to a
    // stable, comparable key (not the raw fetched object) for removal to work.
    additionalProps.getOptionValue = (data) => {
      if (getOptionLabel) {
        try {
          const label = getOptionLabel(data);
          if (label !== undefined && label !== null) return label;
        } catch (e) {}
      }
      return getOptionValue ? getOptionValue(data) : data;
    };
    additionalProps.isOptionSelected = (option, selectValue) => {
      if (!Array.isArray(selectValue) || selectValue.length === 0) return false;
      const optionLabel = getOptionLabel ? getOptionLabel(option) : option;
      return selectValue.some(
        (selected) =>
          selected === option ||
          (getOptionLabel ? getOptionLabel(selected) === optionLabel : false),
      );
    };
    // Keep already-selected options visible (and highlighted) in the menu instead
    // of react-select's default of hiding them once selected.
    additionalProps.hideSelectedOptions = false;
  }

  return (
    <Controller
      render={({ field }) => (
        <div
          style={{ minWidth: '150px', ...style }}
          ref={containerRef}
          className={`${classes.root} ${customStyles} ${additionalProps.menuPortalTarget ? '': "non-portal-target"} position-relative emerald-input autocomplete ${props.isMulti ? 'multi-select-autocomplete' : ''}`}
        >
          <AsyncSelect
            {...field}
            className={
              disabled && validationMessage
                ? 'invalid-select disabled-prop-async-auto-not-label'
                : disabled && !validationMessage
                  ? 'disabled-prop-async-auto'
                  : ''
            }
            menuIsOpen={menuIsOpen}
            onMenuOpen={onMenuOpen}
            closeMenuOnSelect={closeMenuOnSelect}
            closeMenuOnScroll={(e) => {
              return e.target.contains(containerRef.current);
            }}
            onMenuClose={onMenuClose}
            classes={classes}
            styles={selectStyles}
            customStyles={customStyles}
            showSearchIcon={showSearchIcon}
            isSearchable={isSearchable}
            loadOptions={effectiveLoadOptions}
            defaultOptions={defaultOptions}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            // color
            components={{
              Control,
              Menu,
              NoOptionsMessage,
              Option,
              LoadingIndicator: () => null,
              // Rendered directly inside our custom ValueContainer instead.
              Placeholder: () => null,
              SingleValue,
              MultiValue,
              ValueContainer,
              Input,
              DropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            // value={single && single[menuItemId] === '' ? '' : single}
            onChange={handleChange(field)}
            addNewOptions={addNewOptions}
            dropDownbutton={dropDownbutton}
            menuPlacement={'auto'}
            isShrink={isShrink}
            placeholder=""
            isClearable={isClearable}
            isLoading={isLoading}
            error={!!validationMessage}
            label={label}
            required={required}
            isDisabled={disabled}
            ref={(el) => {
              setRefData(el);
            }}
            FormHText={FormHText || ''}
            {...additionalProps}
          />
          {validationMessage ? (
            <FormHelperText
              style={{ color: '#f44336', fontSize: '11px', paddingLeft: 10 }}
            >
              {validationMessage}
            </FormHelperText>
          ) : (
            <FormHelperText style={{ paddingLeft: 10 }}>
              {FormHText}
            </FormHelperText>
          )}
        </div>
      )}
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultVal}
    />
  );
}

export default withStyles(styles, { withTheme: true })(
  AsyncAutocompleteBoxComponent,
);
